import "./static-base";
import "@tento-ui/init";

import "@tento-ui/ui/storybook";
import "@tento-ui/components/grid/grid-layout";
import "@tento-ui/components/sidebar/sidebar-container";
import "@tento-ui/components/docs-nav/docs-nav";
import "@tento-ui/components/icon-button";
import "./component-bounds";
import "./storybook-container";

import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";
import { Link } from "@tento-ui/components/link/link";
import { DocsNav } from "@tento-ui/components/docs-nav/docs-nav";
import {
  SidebarContainer,
  SidebarContainerState,
} from "@tento-ui/components/sidebar/sidebar-container";
import { SidebarExpandButton } from "@tento-ui/components/sidebar/sidebar-expand-button";
import { CSSLength } from "@tento-ui/css-length";
import { DarkMode } from "@tento-ui/dark-mode";
import { AdminControls } from "@tento-ui/dev/admin-controls";
import { DomUtils } from "@tento-ui/dom";
import { $ } from "@tento-ui/dom-selectors";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Hotkeys } from "@tento-ui/hotkeys";
import { Tracing } from "@tento-core/tracing";
import { Routes } from "@tento-ui/routes/routes";
import { VersionInfoPopover } from "@tento-ui/ui/version-info-popover";
import { Catalogue } from "./catalogue";
import { ComponentBounds } from "./component-bounds";
import { StorybookContainer } from "./storybook-container";
import { installUxTraceDoor } from "./ux-trace-door";

// Enable Tracing for storybook
Tracing.installToConsole();
// Preserve the ux-test report event when Lona serves this standalone catalog.
installUxTraceDoor();

Hotkeys.init([
  ["s", () => $<AppRoot>("root").toggleSidebarSticky()],
  ["d", () => DarkMode.toggle()],
  ["b", () => $<AppRoot>("root").toggleComponentBounds()],
  ["=", () => AdminControls.toggleReveal()],
  ["shift-?", () => VersionInfoPopover.showPersistent()],
]);

@component({
  name: "app-root",
})
export class AppRoot extends LonaWebComponent {
  private $storybookContainer: StorybookContainer = this.hydrate(
    "storybook-container",
  );
  private $sidebarContainer: SidebarContainer =
    this.hydrate("sidebar-container");
  private $expandButton: SidebarExpandButton = this.hydrate("expand-button");
  private $sidebarNav: DocsNav = this.hydrate("sidebar-nav");
  private $componentBounds: ComponentBounds = this.hydrate("component-bounds");

  // The catalogue is the charter: `core` is derived from the allowlist and the
  // generated module, `extensions` from the hand-written product tabs. Two
  // modes, one switch — that switch IS the line between the design library and
  // everything built on it.
  private readonly catalogue: Catalogue.Model = Catalogue.build();
  private mode: Catalogue.Mode = "core";
  private modeButtons: Map<Catalogue.Mode, HTMLAnchorElement> = new Map();
  private sectionLinks: Map<string, HTMLAnchorElement> = new Map();
  private readonly sidebarWidth = CSSLength.fromPx(300);
  private sidebarRevealed = false;
  private bindGeneration = 0;

  constructor() {
    super();

    const modes: { mode: Catalogue.Mode; label: string }[] = [
      { mode: "core", label: "Core" },
      { mode: "ext", label: "Extensions" },
    ];
    for (const { mode, label } of modes) {
      const $b = document.createElement("a");
      $b.toggleAttribute("text", true);
      $b.setAttribute("data-nav", mode);
      $b.setAttribute("href", `#lib:${mode}`);
      $b.textContent = label;
      GESTURE_MANAGER.addPointerEvent($b, {
        onClick: (_$captured, _offset, e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
          void this.bindMode(mode, null);
        },
      });
      this.modeButtons.set(mode, $b);
      this.$("top-tab-items").appendChild($b);
    }

    this.$expandButton.onToggleExpanded = (expanded) =>
      this.toggleSidebarSticky(expanded);
    this.$expandButton.onpointerenter = (event) => {
      if (event.pointerType === "touch") return;
      this.$sidebarContainer.toggleFocused("catalogue-toggle-hover", true);
    };
    this.$expandButton.onpointerleave = () => {
      this.$sidebarContainer.toggleFocused("catalogue-toggle-hover", false);
    };
    GESTURE_MANAGER.addPointerEvent(this.$("dark-mode-btn"), {
      onClick: () => DarkMode.toggle(),
    });

    const $boundsButton = this.$("bounds-btn");
    this.$componentBounds.onChange = (enabled) => {
      $boundsButton.toggleAttribute("selected", enabled);
      $boundsButton.setAttribute("aria-pressed", String(enabled));
    };
    this.$componentBounds.bind({
      $scope: this.$storybookContainer,
      $viewport: this.$("root"),
    });
    GESTURE_MANAGER.addPointerEvent($boundsButton, {
      onClick: () => this.toggleComponentBounds(),
    });
  }

  toggleComponentBounds() {
    this.$componentBounds.toggle();
  }

  private syncSidebarLayout(animate: boolean) {
    this.$sidebarContainer.bind({
      viewModel: this.sidebarRevealed
        ? SidebarContainerState.docked({
            width: this.sidebarWidth,
            animate,
            resizeEnabled: false,
          })
        : SidebarContainerState.peek({
            width: this.sidebarWidth,
            animate,
            resizeEnabled: false,
          }),
      delegate: null,
    });
    this.$expandButton.toggleAttribute("revealed", this.sidebarRevealed);
    // The shared container owns its sticky-width variable. Size our content
    // directly so docking cannot inherit a stale value from peek mode.
    this.$("root").style.width = this.sidebarRevealed
      ? `calc(100% - ${this.sidebarWidth.resolve()})`
      : "100%";
  }

  toggleSidebarSticky(force: Option<boolean> = null) {
    const revealed = force ?? !this.sidebarRevealed;
    this.sidebarRevealed = revealed;
    DomUtils.flipAnimation([this.$("description")], () => {
      this.syncSidebarLayout(true);
      this.toggleAttribute("revealed", revealed);
    });
  }

  connectedCallback() {
    // The container suppresses its first paint; keep later hover changes animated.
    this.syncSidebarLayout(true);
    const { mode, section } = AppRoot.resolveHash();
    void this.bindMode(mode, section);
    // A same-document navigation (a pasted link, the back button) changes the
    // hash without reconstructing this element, so the hash is listened to —
    // not just read once. `window` outlives the component: install here,
    // remove on disconnect.
    window.addEventListener("hashchange", this.handleHashChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.$sidebarContainer.toggleFocused("catalogue-toggle-hover", false);
    window.removeEventListener("hashchange", this.handleHashChange);
  }

  private handleHashChange = () => {
    const { mode, section } = AppRoot.resolveHash();
    if (mode !== this.mode) {
      void this.bindMode(mode, section);
    } else if (section != null && !this.sectionLinks.get(section)?.hasAttribute("selected")) {
      this.goToSection(section);
    }
  };

  /** `#lib:core|ext` + `#section:<id>`. The old `#tab:<id>` links keep
   * resolving: `core` and `all` map to a mode, anything else to the extension
   * section of that id — and the legacy key is dropped from the hash. */
  private static resolveHash(): { mode: Catalogue.Mode; section: Option<string> } {
    const qp = Routes.getHashParams();
    const tab = qp.get("tab");
    if (tab != null) {
      qp.delete("tab");
      const legacy: { mode: Catalogue.Mode; section: Option<string> } =
        tab === "core"
          ? { mode: "core", section: null }
          : tab === "all"
            ? { mode: "ext", section: null }
            : { mode: "ext", section: tab };
      qp.set("lib", legacy.mode);
      if (legacy.section != null) qp.set("section", legacy.section);
      AppRoot.replaceHash(qp);
      return legacy;
    }
    const lib = qp.get("lib");
    return {
      mode: lib === "ext" ? "ext" : "core",
      section: qp.get("section") ?? null,
    };
  }

  /** `Routes.setHashParam` can add a key but never remove one. */
  private static replaceHash(params: Map<string, string>): void {
    const hash = [...params.entries()]
      .map(([k, v]) => `${encodeURIComponent(k)}:${encodeURIComponent(v)}`)
      .join("&");
    window.history.replaceState(null, "", `#${hash}`);
  }

  private sections(): Catalogue.Section[] {
    return this.mode === "core"
      ? this.catalogue.core
      : this.catalogue.extensions;
  }

  /** Section jump-links for the current mode: scroll, never rebind. */
  private updateSectionLinks() {
    const $row = this.$("tab-items");
    DomUtils.clearChildren($row);
    this.sectionLinks.clear();
    for (const section of this.sections()) {
      const $b = document.createElement("a");
      $b.toggleAttribute("text", true);
      $b.setAttribute("data-nav", section.id);
      $b.setAttribute("href", `#lib:${this.mode}&section:${section.id}`);
      $b.textContent = section.label;
      GESTURE_MANAGER.addPointerEvent($b, {
        onClick: (_$captured, _offset, e) => {
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
          this.goToSection(section.id);
        },
      });
      this.sectionLinks.set(section.id, $b);
      $row.appendChild($b);
    }
  }

  private selectSection(id: string) {
    Routes.setHashParam("section", id);
    for (const [sid, $b] of this.sectionLinks.entries()) {
      $b.toggleAttribute("selected", sid === id);
    }
    this.$sidebarNav.setActivePath(`${this.mode}/${id}`);
  }

  private goToSection(id: string) {
    this.selectSection(id);
    this.$storybookContainer.scrollToSection(id);
  }

  /** Use the same collapsible groups and row selection as docs and localhost. */
  updateSidebar() {
    const targets = new Map<string, { sectionId: string; index: number }>();
    const items: DocsNav.NavLibrary[] = this.sections().map((section) => {
      const path = `${this.mode}/${section.id}`;
      const labels = [
        ...section.items.map((story) => story.title()),
        ...section.gaps.map((tag) => `${tag} (no example)`),
      ];
      return {
        title: section.label,
        namespace: path,
        path,
        sections: [{
          title: "",
          children: labels.map((label, index) => {
            const itemPath = `${path}/${index}`;
            targets.set(itemPath, { sectionId: section.id, index });
            return { label, path: itemPath, slug: String(index) };
          }),
        }],
      };
    });
    this.$sidebarNav.bind({
      items,
      activePath: "",
      // Expansion belongs to this catalogue, never another docs surface's storage.
      defaultExpanded: true,
      iconForLibrary: null,
      onNavigate: (path) => {
        const target = targets.get(path);
        if (target == null) return;
        this.selectSection(target.sectionId);
        this.$sidebarNav.setActivePath(path);
        this.$storybookContainer.scrollToCard(target.sectionId, target.index);
      },
    });
  }

  async bindMode(mode: Catalogue.Mode, section: Option<string>) {
    const generation = ++this.bindGeneration;
    // Core is filled by the std- charter. Until it has sections, a Core
    // request (the default landing, or an old #tab:core link) lands on
    // Extensions rather than on an empty page.
    if (mode === "core" && this.catalogue.core.length === 0) mode = "ext";
    this.mode = mode;
    const params = Routes.getHashParams();
    params.delete("tab");
    params.set("lib", mode);
    if (section != null) params.set("section", section);
    else params.delete("section");
    AppRoot.replaceHash(params);

    for (const [m, $b] of this.modeButtons.entries()) {
      $b.toggleAttribute("selected", m === mode);
    }
    this.updateSectionLinks();
    this.updateSidebar();

    await this.$storybookContainer.bind({ sections: this.sections() });
    if (generation !== this.bindGeneration) return;
    if (section != null) this.goToSection(section);
  }

  static $styles = [
    Button.$style,
    Link.$style,
    Divider.$hr,
    css`
      :host {
        height: 100%;
      }

      #root {
        padding: 40px;
      }

      #caption {
        color: var(--text-sec);
        --margin-bottom: 12px;
      }

      .section {
        align-items: start;
        justify-content: start;
        width: calc((100% - 8px) / 3);
      }

      @media (max-width: 800px) {
        #title {
          margin-bottom: 8px;
        }

        #title-section {
          flex-direction: column;
        }

        #root {
          padding: 20px;
        }

        .section {
          width: calc((100% - 8px) / 2);
        }

        #description {
          width: 100%;
          margin-bottom: 16px;
        }
      }

      p {
        max-width: 400px;
        color: var(--text-sec);
      }

      #thicc-divider {
        width: 60px;
        height: 6px;
        border-radius: 4px;
        background: #444;
        margin-bottom: 60px;
        flex-shrink: 0;
      }
    `,
    css`
      #expand-button,
      #bounds-btn,
      #dark-mode-btn {
        position: fixed;
        top: 20px;
        z-index: 9500; /* ZIndex.DEV_TOOLS */
      }

      #expand-button {
        left: 30px;
      }

      #bounds-btn,
      #dark-mode-btn {
        width: 36px;
        height: 28px;
      }

      #dark-mode-btn {
        right: 30px;
      }

      /* One button-width plus an 8px gap left of the dark mode toggle. */
      #bounds-btn {
        right: 74px;
      }

      @media (max-width: 800px) {
        #expand-button {
          left: 12px;
        }

        #dark-mode-btn {
          right: 12px;
        }

        #bounds-btn {
          right: 56px;
        }
      }

      #sidebar-container {
        --sidebar-container-top: 0px;
        --sidebar-container-height: 100%;
        --sidebar-container-border-radius: 0px;

        height: 100vh;
        width: 100vw;
        overflow: hidden;
      }

      @media (prefers-reduced-motion: reduce) {
        #sidebar-container {
          --sidebar-container-animation-duration: 0ms;
        }
      }

      #sidebar-frame {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: 0px;
      }

      #sidebar {
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        min-height: 0px;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      #sidebar-toggle-backdrop {
        position: absolute;
        top: 0px;
        left: 0px;
        /* Leave the scrollbar lane outside the fade. */
        right: 20px;
        z-index: 1;
        height: 88px;
        pointer-events: none;
        background: var(--bg);
        mask-image:
          linear-gradient(
            to bottom,
            black 0px,
            rgba(0, 0, 0, 0.9) 20px,
            rgba(0, 0, 0, 0.6) 40px,
            rgba(0, 0, 0, 0.25) 60px,
            rgba(0, 0, 0, 0.06) 76px,
            transparent 88px
          ),
          linear-gradient(to right, black calc(100% - 16px), transparent);
        mask-composite: intersect;
      }

      #sidebar-nav {
        --accordion-header-font-size: var(--p-size);
        --accordion-header-padding: 6px 12px;
        --accordion-header-hover-inset: 0px 8px;
        box-sizing: border-box;
        width: 100%;
        padding: 88px 20px 20px;
        flex-shrink: 0;
      }

      #root {
        height: 100%;
        width: 100%;
        overflow-y: scroll;
      }

      #footer {
        position: absolute;
        bottom: 0px;
        width: 100%;
      }

      #doc-selection a {
        border: none;
        background: none;
        cursor: pointer;
        color: var(--text-ter);
        text-decoration: none;
        position: relative;
        margin-right: 20px;
      }

      #doc-selection a[selected]::before,
      #doc-selection a:hover::before {
        content: "";
        position: absolute;
        bottom: -4px;
        left: 0px;
        right: 0px;
        height: 2px;
        border-radius: 2px;
      }

      #doc-selection a:hover::before {
        background: var(--bg-selected);
      }

      #doc-selection a[selected]::before,
      #doc-selection a:hover[selected]::before {
        background: var(--fg-icon-ter);
      }

      #description {
        --line-height-ratio: 1.4;
      }

      #description > * + * {
        --margin-top: 24px;
      }

      #title {
        font-weight: 400;
      }

      button {
        max-width: 200px;
        align-self: center;
      }

      #tab-groups {
        gap: 8px;
      }

      #tab-items,
      #top-tab-items {
        flex-wrap: wrap;
        gap: 8px;
      }

      #tab-items a,
      #top-tab-items a {
        margin-bottom: 8px;
      }
    `,
  ];

  static $html = html`
    <std-sidebar-expand-button id="expand-button"></std-sidebar-expand-button>
    <std-icon-button
      id="bounds-btn"
      squarish
      aria-label="Toggle component bounds"
      aria-pressed="false"
    >
      <svg fill="none" stroke="currentColor" slot="icon" viewBox="0 0 16 16">
        <rect
          x="1.75"
          y="5.25"
          width="12.5"
          height="9"
          rx="1"
          stroke-width="1.5"
          stroke-dasharray="2.5 2"
        />
        <rect x="1" y="1" width="7.5" height="4.25" rx="1" fill="currentColor" stroke="none" />
      </svg>
    </std-icon-button>
    <std-icon-button id="dark-mode-btn" squarish aria-label="Toggle dark mode">
      <svg fill="currentColor" slot="icon" viewBox="0 0 16 16">
        <path
          d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"
        />
      </svg>
    </std-icon-button>

    <std-sidebar-container id="sidebar-container">
      <std-col id="sidebar-frame" slot="sidebar">
        <std-col id="sidebar">
          <std-docs-nav id="sidebar-nav"></std-docs-nav>
        </std-col>
        <div id="sidebar-toggle-backdrop" aria-hidden="true"></div>
      </std-col>

      <std-col id="root" slot="main-content">
        <std-row id="title-section" style="margin-top:64px;margin-bottom:20px;">
          <std-col class="section">
            <p id="caption" style="margin-left:2px;">Lona</p>
            <h1 id="title">Design Library</h1>
          </std-col>
          <std-spacer></std-spacer>
          <std-col id="description" class="section" style="margin-top:26px;">
            <p>
              A set of beautifully designed components built for the native web.
              Start here then make it your own.
            </p>
            <p>
              You probably shouldn't use this, but feel free to take a look and
              explore.
            </p>
          </std-col>
        </std-row>
        <div id="thicc-divider"></div>
        <std-row id="doc-selection" style="margin-bottom:20px;">
          <std-col id="tab-groups">
            <std-row id="top-tab-items"></std-row>
            <std-row id="tab-items"></std-row>
          </std-col>
        </std-row>
        <std-storybook-container id="storybook-container"></std-storybook-container>
      </std-col>
    </std-sidebar-container>
    <catalogue-component-bounds id="component-bounds"></catalogue-component-bounds>
  `;
}
